import { useMediaQueryWithMount } from "@/lib/hooks/ui/use-media-query";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import { useColumnManager } from "@/lib/column-manager";
import { useUniversalAuth, useGlobalModalStack } from "@wtc/ui";
import { toAuthenticatedUser } from "@/lib/utils/crm/user-adapters";
import { useRef } from "react";

/**
 * Hook that contains ALL hook calls for SimpleTable.
 * CRITICAL: All hooks must be called unconditionally in the same order every render.
 * This fixes the "rendered fewer hooks than expected" error.
 */
export function useSimpleTableHooks() {
  // ============================================================================
  // ALL HOOKS MUST BE CALLED HERE - BEFORE ANY CONDITIONAL LOGIC
  // ============================================================================

  // Mobile detection (SSR-safe). `hasMounted` lets the view layer hold a
  // neutral state until the real viewport is known, preventing a
  // desktop-table-then-mobile-cards flash on first paint.
  const { matches: isMobile, mounted: hasMounted } = useMediaQueryWithMount(
    "(max-width: 768px)"
  );

  // Auth context - Get current user for resolving "me" values
  const { appUser: rawAppUser } = useUniversalAuth();
  const appUser = toAuthenticatedUser(rawAppUser);

  // Field filtering - Role-based field filtering
  const { filterFieldsByRole, roleName } = useFieldFiltering();

  // Column manager - CRITICAL FIX: Always call unconditionally
  // If provider is missing, React will throw a clear error for debugging
  const columnManager = useColumnManager();

  // Global modal stack - CRITICAL FIX: Must be called BEFORE any early returns
  // Previously this was called after the mobile early return, causing hook ordering issues
  const { openModal } = useGlobalModalStack();

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const tableHeaderScrollRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);

  // Extract column manager values with fallback
  const contextVisibleColumns = columnManager?.visibleColumns || [];
  const contextToggleColumn = columnManager?.toggleColumn || (() => {});
  const hiddenColumns = columnManager?.hiddenColumns || [];
  const contextColumnOrder = columnManager?.columnOrder || [];
  const contextSetColumnOrder = columnManager?.setColumnOrder || (() => {});
  // Per-table prefs key (shared with visibility/order) so width + page size
  // persist to the same per-user, per-table namespace in user_prefs.
  const tablePrefsKey = columnManager?.storageKey;

  return {
    isMobile,
    hasMounted,
    appUser: appUser || { id: "", email: "", role: "client_user" as const }, // Fallback for null case
    filterFieldsByRole,
    roleName,
    contextVisibleColumns,
    contextToggleColumn,
    hiddenColumns,
    contextColumnOrder,
    contextSetColumnOrder,
    tablePrefsKey,
    openModal,
    containerRef,
    tableHeaderScrollRef,
    tableBodyRef,
  };
}
